/**
 * Shared Program Selector Component
 *
 * Provides consistent program selection with data isolation
 * for use across dashboard tabs (Financial, Goals, Performance, etc.)
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getWeaveinApiUrl } from '@/src/config';

import { useAppTranslation } from '@/contexts/TranslationContext';
interface Program {
  id: number;
  name: string;
  program_type: string;
  is_active: boolean;
}

interface ProgramSelectorProps {
  selectedProgramId: number | null;
  onProgramChange: (programId: number) => void;
  sponsorId?: number;
  showAllOption?: boolean;
  label?: string;
  className?: string;
}

const ProgramSelector: React.FC<ProgramSelectorProps> = ({
  selectedProgramId,
  onProgramChange,
  sponsorId,
  showAllOption = false,
  label = 'Select Program',
  className = ''
}) => {
  const { t } = useAppTranslation();
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPrograms();
  }, [sponsorId]);

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      const storedSponsorId = localStorage.getItem('active_sponsor_id');

      // Require CBO ID - don't fall back to hardcoded values
      if (!cboId) {
        setError('No organization context found');
        setLoading(false);
        return;
      }

      // Build query params for data isolation
      let queryParams = `cbo_id=${cboId}`;
      const effectiveSponsorId = sponsorId || storedSponsorId;
      if (effectiveSponsorId) {
        queryParams += `&sponsor_id=${effectiveSponsorId}`;
      }

      const response = await axios.get(
        getWeaveinApiUrl(`/api/programs?${queryParams}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': cboId
          }
        }
      );

      // Filter to only show programs belonging to this organization
      const filteredPrograms = response.data.filter((program: Program) => {
        return program.is_active !== false; // Show active and undefined (legacy)
      });

      setPrograms(filteredPrograms);

      // Auto-select first program if none selected and we have programs
      if (filteredPrograms.length > 0 && !selectedProgramId) {
        onProgramChange(filteredPrograms[0].id);
      }
    } catch (err: any) {
      console.error('Failed to fetch programs:', err);
      setError(err.response?.data?.detail || 'Failed to load programs');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}:</label>
        <div className="animate-pulse bg-gray-200 h-10 w-48 rounded-md"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}:</label>
        <span className="text-sm text-red-600">{error}</span>
      </div>
    );
  }

  if (programs.length === 0) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}:</label>
        <span className="text-sm text-gray-500 dark:text-gray-400">{t('shared.no_programs_available', 'No programs available')}</span>
      </div>
    );
  }

  // Show program name if only one program (instead of hiding completely)
  if (programs.length === 1 && !showAllOption) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}:</label>
        <span className="text-sm font-semibold text-gray-900 dark:text-white">
          {programs[0].name}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          ({programs[0].program_type})
        </span>
      </div>
    );
  }

  return (
    <div className={`flex items-center space-x-4 ${className}`}>
      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}:</label>
      <select
        value={selectedProgramId || ''}
        onChange={(e) => onProgramChange(parseInt(e.target.value))}
        className="flex-1 max-w-md px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-800"
      >
        {showAllOption && (
          <option value="">{t('shared.all_programs', 'All Programs')}</option>
        )}
        {programs.map((program) => (
          <option key={program.id} value={program.id}>
            {program.name} ({program.program_type})
          </option>
        ))}
      </select>
    </div>
  );
};

export default ProgramSelector;
